import * as Tone from "tone";
import { parseChordToken } from "./romanParser";
import { ChordProgression } from "../components/sequencer/types";

/**
 * Register type representing a range of notes
 */
export interface Register {
  bottomNote: string; // e.g., "C3"
  topNote: string; // e.g., "C4"
}

/**
 * Convert a note name with octave (e.g., "C3") to MIDI number
 */
export function noteToMidi(note: string): number {
  return Tone.Frequency(note).toMidi();
}

/**
 * Convert a MIDI number to note name with octave (e.g., 60 -> "C4")
 */
export function midiToNote(midi: number): string {
  return Tone.Frequency(midi, "midi").toNote();
}

/**
 * Get the height of a register in semitones
 */
export function getRegisterHeight(register: Register): number {
  const bottomMidi = noteToMidi(register.bottomNote);
  const topMidi = noteToMidi(register.topNote);
  return topMidi - bottomMidi;
}

/**
 * Adjust a register up or down by a number of semitones
 */
export function adjustRegister(
  register: Register,
  semitones: number
): Register {
  const bottomMidi = noteToMidi(register.bottomNote);
  const topMidi = noteToMidi(register.topNote);

  return {
    bottomNote: midiToNote(bottomMidi + semitones),
    topNote: midiToNote(topMidi + semitones),
  };
}

/**
 * Get a key-based default register (e.g., C major = C3>C4)
 */
export function getKeyBasedRegister(key: string): Register {
  // Remove any mode suffix (e.g., "Cm" -> "C")
  const rootNote = key.replace(/[mM]$/, "");
  return {
    bottomNote: `${rootNote}3`,
    topNote: `${rootNote}4`,
  };
}

/**
 * Check if a chord token has a 9th
 */
function hasNinthInChordToken(token: any): boolean {
  // Check for 9th in extraOffsets (offset 14 semitones, or altered 9ths: 13, 15)
  if (token.extraOffsets) {
    if (
      token.extraOffsets.includes(14) ||
      token.extraOffsets.includes(13) ||
      token.extraOffsets.includes(15)
    ) {
      return true;
    }
  }
  // Check for bass degree 9
  if (token.bassDegree === 9) {
    return true;
  }
  return false;
}

/**
 * Check if any chord in a progression has a 9th
 */
export function hasNinthInProgression(
  progression: ChordProgression
): boolean {
  for (const roman of progression.roman) {
    try {
      const token = parseChordToken(roman);
      if (hasNinthInChordToken(token)) {
        return true;
      }
    } catch (error) {
      // If parsing fails, continue checking other chords
      continue;
    }
  }
  return false;
}


/**
 * Get the pitch class (0-11) of a MIDI note
 */
function getPitchClass(midi: number): number {
  return midi % 12;
}


/**
 * Get all octave positions of a pitch class within a register
 */
function getOctavePositionsInRegister(
  pitchClass: number,
  register: Register
): number[] {
  const bottomMidi = noteToMidi(register.bottomNote);
  const topMidi = noteToMidi(register.topNote);
  const positions: number[] = [];

  // Find the lowest octave that could contain this pitch class
  let currentMidi = bottomMidi;
  while (currentMidi % 12 !== pitchClass && currentMidi <= topMidi) {
    currentMidi++;
  }

  // If we found a valid starting point, collect all octave positions
  if (currentMidi <= topMidi) {
    while (currentMidi <= topMidi) {
      positions.push(currentMidi);
      currentMidi += 12; // Move to next octave
    }
  }

  return positions;
}

/**
 * Generate all valid voicings for a chord within a register
 */
function generateVoicings(
  chordMidi: number[],
  register: Register,
  hasNinth: boolean
): number[][] {
  const bottomMidi = noteToMidi(register.bottomNote);
  const topMidi = noteToMidi(register.topNote);
  const rootMidi = chordMidi[0];

  // Get unique pitch classes from the chord
  const pitchClasses = Array.from(new Set(chordMidi.map(getPitchClass)));
  
  // Identify 9th pitch class if present
  const ninthPitchClass = hasNinth
    ? pitchClasses.find((pc) => {
        const rootPc = getPitchClass(rootMidi);
        return (pc - rootPc + 12) % 12 === 2;
      })
    : undefined;

  // For each pitch class, get all octave positions in register
  const positionsByPitchClass: Map<number, number[]> = new Map();
  for (const pc of pitchClasses) {
    const positions = getOctavePositionsInRegister(pc, register);
    if (positions.length > 0) {
      positionsByPitchClass.set(pc, positions);
    }
  }

  // Generate all combinations including all possible doublings
  const voicings: number[][] = [];

  // Generate all combinations of positions for each pitch class
  // For non-9th notes: include all possible combinations (all doublings)
  // For 9th: include only 0 or 1 position (preferring higher)
  function generateAllCombinations(
    pitchClassIndex: number,
    currentVoicing: number[]
  ) {
    if (pitchClassIndex >= pitchClasses.length) {
      // Check if this voicing has all required chord tones
      const voicingPitchClasses = new Set(currentVoicing.map(getPitchClass));
      let hasAllTones = true;
      
      const pitchClassesArray = Array.from(pitchClasses);
      for (const pc of pitchClassesArray) {
        if (!voicingPitchClasses.has(pc)) {
          hasAllTones = false;
          break;
        }
      }

      // Check 9th constraint: only one 9th if present
      if (hasAllTones && ninthPitchClass !== undefined) {
        const ninthCount = currentVoicing.filter(
          (midi) => getPitchClass(midi) === ninthPitchClass
        ).length;
        if (ninthCount !== 1) {
          hasAllTones = false;
        }
      }

      if (hasAllTones) {
        const sorted = [...currentVoicing].sort((a, b) => a - b);
        // Check if all notes are within register
        if (sorted.length > 0 && sorted[0] >= bottomMidi && sorted[sorted.length - 1] <= topMidi) {
          voicings.push(sorted);
        }
      }
      return;
    }

    const pc = pitchClasses[pitchClassIndex];
    const positions = positionsByPitchClass.get(pc);

    if (!positions || positions.length === 0) {
      // No positions available for this pitch class, skip it
      generateAllCombinations(pitchClassIndex + 1, currentVoicing);
      return;
    }

    const isNinthPc = ninthPitchClass !== undefined && pc === ninthPitchClass;

    if (isNinthPc) {
      // For 9th: only add one instance (prefer higher), or none
      // We need at least one 9th, so always add the highest one
      const highestNinth = positions[positions.length - 1];
      generateAllCombinations(pitchClassIndex + 1, [...currentVoicing, highestNinth]);
    } else {
      // For non-9th notes: generate all combinations of positions (all doublings)
      // This includes: 1 position, 2 positions, ... all positions
      // We use a power set approach - generate all subsets
      // Use function expression instead of declaration for ES5 compatibility
      const generatePositionCombinations = (
        positionIndex: number,
        selectedPositions: number[]
      ) => {
        // positions is guaranteed to be defined here due to the check above
        const posArray = positions!;
        if (positionIndex >= posArray.length) {
          // We've considered all positions
          // Only continue if we've selected at least one position (we need the pitch class)
          if (selectedPositions.length > 0) {
            generateAllCombinations(pitchClassIndex + 1, [...currentVoicing, ...selectedPositions]);
          }
          return;
        }

        // Option 1: Don't include this position
        generatePositionCombinations(positionIndex + 1, selectedPositions);

        // Option 2: Include this position (allows doublings)
        generatePositionCombinations(positionIndex + 1, [...selectedPositions, posArray[positionIndex]]);
      };

      generatePositionCombinations(0, []);
    }
  }

  generateAllCombinations(0, []);

  return voicings;
}

/**
 * Voice a chord within a register, choosing the inversion that minimizes top note motion
 */
export function voiceChordInRegister(
  chordMidi: number[],
  register: Register,
  previousTopNote?: number
): number[] {
  if (chordMidi.length === 0) {
    return [];
  }

  const bottomMidi = noteToMidi(register.bottomNote);
  const topMidi = noteToMidi(register.topNote);

  // Check if chord has a 9th by looking at pitch classes
  const rootMidi = chordMidi[0];
  const rootPc = getPitchClass(rootMidi);
  const hasNinth = chordMidi.some((midi) => {
    if (midi === rootMidi) return false;
    const pc = getPitchClass(midi);
    return (pc - rootPc + 12) % 12 === 2;
  });

  // Generate all valid voicings
  const voicings = generateVoicings(chordMidi, register, hasNinth);

  if (voicings.length === 0) {
    // Fallback: try to fit chord as best as possible
    const fallback: number[] = [];
    for (const midi of chordMidi) {
      // Find the closest octave position within register
      let bestMidi = midi;
      let bestDistance = Math.abs(midi - (bottomMidi + topMidi) / 2);

      for (let octave = -2; octave <= 2; octave++) {
        const candidate = midi + octave * 12;
        if (candidate >= bottomMidi && candidate <= topMidi) {
          const distance = Math.abs(candidate - (bottomMidi + topMidi) / 2);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestMidi = candidate;
          }
        }
      }

      if (bestMidi >= bottomMidi && bestMidi <= topMidi) {
        fallback.push(bestMidi);
      }
    }

    return fallback.length > 0 ? fallback.sort((a, b) => a - b) : [chordMidi[0]];
  }

  // Select voicing with top note closest to previous top note
  // When multiple voicings have the same top note distance, prefer ones with more notes (all doublings)
  if (previousTopNote !== undefined) {
    let bestVoicing = voicings[0];
    let bestDistance = Math.abs(
      voicings[0][voicings[0].length - 1] - previousTopNote
    );
    let bestNoteCount = voicings[0].length;

    for (const voicing of voicings) {
      const topNote = voicing[voicing.length - 1];
      const distance = Math.abs(topNote - previousTopNote);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestVoicing = voicing;
        bestNoteCount = voicing.length;
      } else if (distance === bestDistance && voicing.length > bestNoteCount) {
        // If same distance, prefer voicing with more notes (includes all doublings)
        bestVoicing = voicing;
        bestNoteCount = voicing.length;
      }
    }

    return bestVoicing;
  }

  // If no previous top note, prefer voicings with more notes (includes all doublings)
  // But for single-note chords, just return one note
  if (chordMidi.length === 1) {
    // For single note, return the highest position in register
    const singleNoteVoicings = voicings.filter((v) => v.length === 1);
    if (singleNoteVoicings.length > 0) {
      return singleNoteVoicings.reduce((best, current) => {
        return current[0] > best[0] ? current : best;
      });
    }
  }

  // For multi-note chords, prefer voicings with more notes (includes all doublings)
  // Among voicings with the same number of notes, prefer higher top notes
  return voicings.reduce((best, current) => {
    if (current.length > best.length) {
      return current; // Prefer more notes (all doublings included)
    } else if (current.length === best.length) {
      // If same number of notes, prefer higher top note
      const bestTop = best[best.length - 1];
      const currentTop = current[current.length - 1];
      return currentTop > bestTop ? current : best;
    }
    return best;
  });
}
